import * as THREE from "three";

import type { LoomAuthoringDocument } from "./loomAuthoringModel";
import { buildLoomAuthoringSelectionScene } from "./loomAuthoringPreview";
import type { LoomExplicitTrimSurface } from "./loomExplicitTrimPreview";
import {
  buildLoomOwnedSurfacePresentation,
  type LoomOwnedDisplaySurface,
  type LoomSurfacePresentationResult,
  type LoomViewportPresentationState,
} from "./loomViewportPresentation";

export interface LoomLivePreviewOverlayReceipt {
  acceptedSurfaceCount: 1;
  candidateSurfaceCount: 1;
  mode: "amber-candidate-over-ghost-accepted";
}

function candidateSurface(preview: LoomExplicitTrimSurface): LoomOwnedDisplaySurface {
  return {
    faceEntityIds: preview.faceEntityIds,
    faces: preview.faces,
    geometrySource: "retained-display-faces",
    positions: preview.positions,
  };
}

function styleCandidate(group: THREE.Group, color: string): void {
  group.name = "Provisional amber aircraft candidate";
  group.userData.loomLivePreviewRole = "provisional-candidate";
  group.traverse((object) => {
    const mesh = object as THREE.Mesh<THREE.BufferGeometry, THREE.Material | THREE.Material[]>;
    if (!mesh.isMesh) return;
    const style = (source: THREE.Material): THREE.Material => {
      const material = source.clone() as THREE.MeshPhysicalMaterial;
      material.color?.set(color);
      material.depthWrite = false;
      material.emissive?.set(color);
      material.emissiveIntensity = 0.24;
      material.opacity = 0.52;
      material.polygonOffset = true;
      material.polygonOffsetFactor = -1;
      material.polygonOffsetUnits = -1;
      material.transparent = true;
      material.userData = { ...material.userData, loomLivePreview: "visual-only" };
      return material;
    };
    mesh.material = Array.isArray(mesh.material)
      ? mesh.material.map(style) : style(mesh.material);
    mesh.renderOrder = 24;
  });
}

function styleAcceptedBaseline(group: THREE.Group): void {
  group.name = "Ghosted last accepted aircraft";
  group.userData.loomLivePreviewRole = "last-accepted";
  group.traverse((object) => {
    const mesh = object as THREE.Mesh<THREE.BufferGeometry, THREE.Material | THREE.Material[]>;
    if (!mesh.isMesh) return;
    const style = (source: THREE.Material): THREE.Material => {
      const material = source.clone();
      material.depthWrite = false;
      material.opacity = 0.34;
      material.transparent = true;
      material.userData = { ...material.userData, loomLivePreview: "last-accepted" };
      return material;
    };
    mesh.material = Array.isArray(mesh.material)
      ? mesh.material.map(style) : style(mesh.material);
    mesh.renderOrder = 1;
  });
}

export function addLoomLivePreviewOverlay(
  root: THREE.Group,
  main: LoomSurfacePresentationResult,
  accepted: LoomOwnedDisplaySurface,
  preview: LoomExplicitTrimSurface | null,
  document: LoomAuthoringDocument,
  selectedNodeId: string,
  viewport: LoomViewportPresentationState,
  mainIsAccepted: boolean,
  acceptedColor: string,
  candidateColor: string,
): LoomLivePreviewOverlayReceipt {
  if (mainIsAccepted) {
    styleAcceptedBaseline(main.group);
    const group = preview ? buildLoomOwnedSurfacePresentation(
      candidateSurface(preview), document, selectedNodeId, viewport,
      "ghost-inspection", candidateColor, candidateColor,
    ).group : buildLoomAuthoringSelectionScene(
      document, selectedNodeId, candidateColor, "edited-preview", candidateColor,
    );
    styleCandidate(group, candidateColor);
    root.add(group);
  } else {
    const baseline = buildLoomOwnedSurfacePresentation(
      accepted, document, "", viewport, "ghost-inspection",
      acceptedColor, candidateColor,
    );
    styleAcceptedBaseline(baseline.group);
    root.add(baseline.group);
    styleCandidate(main.group, candidateColor);
  }
  return {
    acceptedSurfaceCount: 1,
    candidateSurfaceCount: 1,
    mode: "amber-candidate-over-ghost-accepted",
  };
}
